import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CheckCircle, Mail, MessageSquare, GraduationCap,
  CreditCard, Phone, ArrowRight, Loader2, BookOpen,
  AlertCircle, Rocket, ExternalLink, RefreshCw, Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.png';

// ─── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  { key: 'account',   label: 'Create Account' },
  { key: 'leveltest', label: 'Level Test'      },
  { key: 'plan',      label: 'Choose Plan'     },
  { key: 'access',    label: "You're In!"      },
];

// Map funnel_status → which step index is active (0-based)
const getFunnelStep = (funnelStatus) => {
  switch (funnelStatus) {
    case 'registered':                         return 1; // step 2 active
    case 'level_tested':                       return 2; // step 3 active
    case 'consultant_requested':               return 2; // step 3 active (waiting)
    case 'awaiting_payment':                   return 2; // step 3 active (processing)
    case 'approved':
    case 'onboarding_done':
    case 'active':                             return 3; // step 4 active
    case 'payment_expired':                    return 3; // renewal
    default:                                   return 1;
  }
};

// ─── Step progress bar ─────────────────────────────────────────────────────────
const StepProgress = ({ funnelStatus }) => {
  const activeStep = getFunnelStep(funnelStatus);

  return (
    <div className="px-6 pt-6 pb-2">
      <div className="flex items-center justify-between relative">
        {/* Connector line behind steps */}
        <div className="absolute left-0 right-0 top-4 h-0.5 bg-[#E8EAF0] z-0" />
        <div
          className="absolute left-0 top-4 h-0.5 bg-[#4CAF50] z-0 transition-all duration-500"
          style={{ width: `${Math.min(100, (activeStep / (STEPS.length - 1)) * 100)}%` }}
        />

        {STEPS.map((step, i) => {
          const isDone    = i < activeStep;
          const isCurrent = i === activeStep;
          return (
            <div key={step.key} className="flex flex-col items-center gap-1.5 z-10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                isDone
                  ? 'bg-[#4CAF50] border-[#4CAF50] text-white'
                  : isCurrent
                    ? 'bg-white border-[#1A2E6E] text-[#1A2E6E]'
                    : 'bg-white border-[#E8EAF0] text-[#1A2E6E]/30'
              }`}>
                {isDone ? <CheckCircle size={16} /> : i + 1}
              </div>
              <span className={`text-[10px] font-semibold text-center leading-tight max-w-[56px] ${
                isDone
                  ? 'text-[#4CAF50]'
                  : isCurrent
                    ? 'text-[#1A2E6E]'
                    : 'text-[#1A2E6E]/30'
              }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Shared shell ──────────────────────────────────────────────────────────────
const Shell = ({ children, funnelStatus, showProgress = true }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] via-[#FDF8F3] to-[#FAFAF7] flex items-center justify-center px-4 py-12">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#F5A623]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1A2E6E]/5 rounded-full blur-3xl" />
    </div>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-lg relative z-10"
    >
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-[#1A2E6E]/10 overflow-hidden">
        {/* Logo */}
        <div className="px-8 pt-7 pb-0 flex justify-center">
          <Link to="/">
            <img src={LOGO_URL} alt="Frankly Talking English" className="h-11 w-auto" />
          </Link>
        </div>

        {/* Step progress */}
        {showProgress && funnelStatus && (
          <StepProgress funnelStatus={funnelStatus} />
        )}

        {children}
      </div>
    </motion.div>
  </div>
);

// ─── State: registered — hasn't done level test yet ────────────────────────────
const ViewRegistered = ({ contactEmail, stripeEnabled, whatsappUrl, onConsultantRequest, requesting }) => (
  <div className="p-8 space-y-5">
    <div className="text-center">
      <div className="w-16 h-16 bg-[#1A2E6E]/8 rounded-2xl flex items-center justify-center mx-auto mb-3">
        <Rocket className="w-8 h-8 text-[#1A2E6E]" />
      </div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Let's get you started!</h2>
      <p className="text-[#1A2E6E]/60 mt-1.5 text-sm">Choose how you'd like to begin:</p>
    </div>

    <div className="space-y-3">
      {/* Option A — level test (recommended) */}
      <Link
        to="/student/level-test"
        className="p-4 rounded-2xl border-2 border-[#D72B2B] bg-[#D72B2B]/3 hover:bg-[#D72B2B]/8 transition-all group flex items-center gap-4"
      >
        <div className="w-11 h-11 bg-[#D72B2B]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#D72B2B]/20 transition-colors">
          <GraduationCap className="w-6 h-6 text-[#D72B2B]" />
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <p className="font-bold text-[#1A2E6E] text-sm">Take the Level Test</p>
            <span className="text-[10px] bg-[#D72B2B] text-white px-1.5 py-0.5 rounded-full font-semibold">Recommended</span>
          </div>
          <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Free 10-min test · get a personalised plan</p>
        </div>
        <ArrowRight size={18} className="text-[#D72B2B] shrink-0" />
      </Link>

      {/* Option B — pay online */}
      {stripeEnabled ? (
        <Link
          to="/student/checkout"
          className="p-4 rounded-2xl border-2 border-[#4CAF50] bg-[#4CAF50]/3 hover:bg-[#4CAF50]/8 transition-all group flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#4CAF50]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#4CAF50]/20 transition-colors">
            <CreditCard className="w-6 h-6 text-[#4CAF50]" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Choose a plan & pay now</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Instant activation via Stripe</p>
          </div>
          <ArrowRight size={18} className="text-[#4CAF50] shrink-0" />
        </Link>
      ) : null}

      {/* Option C — talk to consultant / WhatsApp */}
      {whatsappUrl ? (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-4 rounded-2xl border-2 border-[#1A2E6E]/20 bg-[#1A2E6E]/3 hover:bg-[#1A2E6E]/8 transition-all group flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#1A2E6E]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#1A2E6E]/20 transition-colors">
            <Phone className="w-6 h-6 text-[#1A2E6E]" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Talk to a consultant</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Get advice & discuss plan options</p>
          </div>
          <ExternalLink size={16} className="text-[#1A2E6E]/40 shrink-0" />
        </a>
      ) : (
        <button
          onClick={onConsultantRequest}
          disabled={requesting}
          className="p-4 rounded-2xl border-2 border-[#1A2E6E]/20 bg-[#1A2E6E]/3 hover:bg-[#1A2E6E]/8 transition-all text-left group w-full flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#1A2E6E]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#1A2E6E]/20 transition-colors">
            {requesting
              ? <Loader2 className="w-6 h-6 text-[#1A2E6E] animate-spin" />
              : <Phone className="w-6 h-6 text-[#1A2E6E]" />}
          </div>
          <div className="text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Talk to a consultant</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Our team will reach out within 24h</p>
          </div>
        </button>
      )}
    </div>

    <div className="text-center pt-1">
      <Link
        to="/student/chat"
        className="inline-flex items-center gap-1.5 text-xs text-[#1A2E6E]/50 hover:text-[#D72B2B] transition-colors"
      >
        <MessageSquare size={12} /> Have questions? Chat with us
      </Link>
    </div>
  </div>
);

// ─── State: level_tested — choose next step ────────────────────────────────────
const ViewLevelTested = ({ user, contactEmail, onConsultantRequest, requesting, stripeEnabled, whatsappUrl }) => (
  <div className="p-8 space-y-5">
    <div className="text-center">
      <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
        <CheckCircle className="w-8 h-8 text-[#4CAF50]" />
      </div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Level test complete!</h2>
      <p className="text-[#1A2E6E]/60 mt-1.5 text-sm">Now choose how you'd like to get started:</p>
    </div>

    <div className="space-y-3">
      {/* Option A — pay online */}
      {stripeEnabled ? (
        <Link
          to="/student/checkout"
          className="p-4 rounded-2xl border-2 border-[#4CAF50] bg-[#4CAF50]/3 hover:bg-[#4CAF50]/8 transition-all group flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#4CAF50]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#4CAF50]/20 transition-colors">
            <CreditCard className="w-6 h-6 text-[#4CAF50]" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Pay online now</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Instant activation via Stripe</p>
          </div>
          <ArrowRight size={18} className="text-[#4CAF50] shrink-0" />
        </Link>
      ) : (
        <div className="p-4 rounded-2xl border-2 border-[#E8EAF0] opacity-50 cursor-not-allowed flex items-center gap-4">
          <div className="w-11 h-11 bg-[#4CAF50]/10 rounded-xl flex items-center justify-center shrink-0">
            <CreditCard className="w-6 h-6 text-[#4CAF50]" />
          </div>
          <div className="text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Pay online</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Online checkout coming soon</p>
          </div>
        </div>
      )}

      {/* Option B — talk to consultant / WhatsApp */}
      {whatsappUrl ? (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-4 rounded-2xl border-2 border-[#1A2E6E] bg-[#1A2E6E]/3 hover:bg-[#1A2E6E]/8 transition-all group flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#1A2E6E]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#1A2E6E]/20 transition-colors">
            <Phone className="w-6 h-6 text-[#1A2E6E]" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Talk to a consultant</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Get advice & discuss plan options</p>
          </div>
          <ExternalLink size={16} className="text-[#1A2E6E]/40 shrink-0" />
        </a>
      ) : (
        <button
          onClick={onConsultantRequest}
          disabled={requesting}
          className="p-4 rounded-2xl border-2 border-[#1A2E6E] bg-[#1A2E6E]/3 hover:bg-[#1A2E6E]/8 transition-all text-left group w-full flex items-center gap-4"
        >
          <div className="w-11 h-11 bg-[#1A2E6E]/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-[#1A2E6E]/20 transition-colors">
            {requesting
              ? <Loader2 className="w-6 h-6 text-[#1A2E6E] animate-spin" />
              : <Phone className="w-6 h-6 text-[#1A2E6E]" />}
          </div>
          <div className="text-left">
            <p className="font-bold text-[#1A2E6E] text-sm">Request a consultant</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Our team will reach out within 24h</p>
          </div>
        </button>
      )}
    </div>

    <div className="text-center pt-1">
      <Link
        to="/student/chat"
        className="inline-flex items-center gap-1.5 text-xs text-[#1A2E6E]/50 hover:text-[#D72B2B] transition-colors"
      >
        <MessageSquare size={12} /> Have questions? Chat with us
      </Link>
    </div>
  </div>
);

// ─── State: consultant_requested ──────────────────────────────────────────────
const ViewConsultantRequested = ({ contactEmail }) => (
  <div className="p-8 text-center space-y-5">
    <div className="w-16 h-16 bg-[#F5A623]/10 rounded-2xl flex items-center justify-center mx-auto">
      <Clock className="w-8 h-8 text-[#F5A623]" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">We'll be in touch!</h2>
      <p className="text-[#1A2E6E]/60 mt-2 text-sm">
        A consultant will contact you within 24 hours to discuss your plan options.
      </p>
    </div>
    <div className="bg-[#FAFAF7] rounded-xl p-4 text-left space-y-2.5">
      {[
        'Consultant will contact you by email or WhatsApp',
        'You can ask any questions about courses and pricing',
        'Once agreed, your account will be fully activated',
      ].map(item => (
        <div key={item} className="flex items-center gap-2.5">
          <CheckCircle className="w-4 h-4 text-[#4CAF50] shrink-0" />
          <span className="text-sm text-[#1A2E6E]/70">{item}</span>
        </div>
      ))}
    </div>
    <Link
      to="/student/chat"
      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1A2E6E] text-white rounded-xl font-medium hover:bg-[#16275C] transition-all w-full text-sm"
    >
      <MessageSquare size={16} /> Open Support Chat
    </Link>
    <p className="text-xs text-[#1A2E6E]/40">
      Prefer email?{' '}
      <a href={`mailto:${contactEmail}`} className="text-[#D72B2B] hover:underline">{contactEmail}</a>
    </p>
  </div>
);

// ─── State: awaiting_payment ──────────────────────────────────────────────────
const ViewAwaitingPayment = ({ contactEmail }) => (
  <div className="p-8 text-center space-y-5">
    <div className="w-16 h-16 bg-[#1A2E6E]/8 rounded-2xl flex items-center justify-center mx-auto">
      <CreditCard className="w-8 h-8 text-[#1A2E6E]" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Confirming your payment</h2>
      <p className="text-[#1A2E6E]/60 mt-2 text-sm">
        This usually takes just a few seconds. Your account will be activated automatically.
      </p>
    </div>
    <div className="flex items-center justify-center gap-2 text-sm text-[#1A2E6E]/60 py-2">
      <Loader2 size={16} className="animate-spin text-[#D72B2B]" /> Checking payment status…
    </div>
    <Link
      to="/student/chat"
      className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#1A2E6E]/20 text-[#1A2E6E] rounded-xl font-medium hover:bg-[#1A2E6E]/5 transition-all w-full text-sm"
    >
      <MessageSquare size={16} /> Contact support
    </Link>
    <p className="text-xs text-[#1A2E6E]/40">
      Taking too long?{' '}
      <a href={`mailto:${contactEmail}`} className="text-[#D72B2B] hover:underline">Email us</a>
    </p>
  </div>
);

// ─── State: generic pending (manual review) ───────────────────────────────────
const ViewGenericPending = ({ contactEmail }) => (
  <div className="p-8 text-center space-y-5">
    <div className="w-16 h-16 bg-[#F5A623]/10 rounded-2xl flex items-center justify-center mx-auto">
      <Clock className="w-8 h-8 text-[#F5A623]" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Account under review</h2>
      <p className="text-[#1A2E6E]/60 mt-2 text-sm">
        Our team is reviewing your registration. You'll receive full access once approved.
      </p>
    </div>
    <div className="bg-[#FAFAF7] rounded-xl p-4 text-left space-y-2.5">
      {[
        'Team will review your registration details',
        "You'll get full access to classes and materials once approved",
        'Use the support chat while you wait',
      ].map(item => (
        <div key={item} className="flex items-center gap-2.5">
          <CheckCircle className="w-4 h-4 text-[#4CAF50] shrink-0" />
          <span className="text-sm text-[#1A2E6E]/70">{item}</span>
        </div>
      ))}
    </div>
    <Link
      to="/student/chat"
      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1A2E6E] text-white rounded-xl font-medium hover:bg-[#16275C] transition-all w-full text-sm"
    >
      <MessageSquare size={16} /> Open Support Chat
    </Link>
    <div className="flex items-center justify-center gap-2 text-sm text-[#1A2E6E]/60">
      <Mail size={14} />
      <a href={`mailto:${contactEmail}`} className="text-[#D72B2B] hover:underline">{contactEmail}</a>
    </div>
  </div>
);

// ─── State: payment_expired ───────────────────────────────────────────────────
const ViewPaymentExpired = ({ contactEmail, stripeEnabled }) => (
  <div className="p-8 text-center space-y-5">
    <div className="w-16 h-16 bg-[#D72B2B]/10 rounded-2xl flex items-center justify-center mx-auto">
      <AlertCircle className="w-8 h-8 text-[#D72B2B]" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Plan expired</h2>
      <p className="text-[#1A2E6E]/60 mt-2 text-sm">
        Your credits have been used up or your plan expired. Renew to continue learning.
      </p>
    </div>
    {stripeEnabled ? (
      <Link
        to="/student/checkout"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D72B2B] text-white rounded-xl font-semibold hover:bg-[#B71C1C] transition-colors w-full text-sm"
      >
        <RefreshCw size={16} /> Renew my plan
      </Link>
    ) : (
      <a
        href={`mailto:${contactEmail}?subject=Plan Renewal`}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D72B2B] text-white rounded-xl font-semibold hover:bg-[#B71C1C] transition-colors w-full text-sm"
      >
        <Mail size={16} /> Contact us to renew
      </a>
    )}
    <Link
      to="/student/chat"
      className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#1A2E6E]/20 text-[#1A2E6E] rounded-xl font-medium hover:bg-[#1A2E6E]/5 transition-all w-full text-sm"
    >
      <MessageSquare size={16} /> Chat with support
    </Link>
  </div>
);

// ─── State: suspended ─────────────────────────────────────────────────────────
const ViewSuspended = ({ contactEmail }) => (
  <div className="p-8 text-center space-y-5">
    <div className="w-16 h-16 bg-[#D72B2B]/10 rounded-2xl flex items-center justify-center mx-auto">
      <AlertCircle className="w-8 h-8 text-[#D72B2B]" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Account Suspended</h2>
      <p className="text-[#1A2E6E]/60 mt-2 text-sm">
        Your account has been suspended. Please contact us to resolve this.
      </p>
    </div>
    <a
      href={`mailto:${contactEmail}?subject=Account Suspended`}
      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1A2E6E] text-white rounded-xl font-medium hover:bg-[#16275C] transition-all w-full text-sm"
    >
      <Mail size={16} /> Contact Support
    </a>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────
export const StudentPending = () => {
  const { user, funnelStatus, refreshUser } = useAuth();
  const { settings } = usePublicSettings();
  const navigate = useNavigate();
  const contactEmail = settings.contact_email || 'franklytalkingenglish@gmail.com';
  const stripeEnabled = !!settings.stripe_enabled;
  const whatsappUrl = settings.whatsapp_url || null;
  const [requesting, setRequesting] = useState(false);

  const handleConsultantRequest = async () => {
    setRequesting(true);
    try {
      await axios.post(`${API_URL}/api/student/request-consultant`, { message: null });
      toast.success("Request sent! We'll contact you shortly.");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  // Show step progress only for the registration funnel states
  const showProgress = ['registered', 'level_tested', 'consultant_requested', 'awaiting_payment'].includes(funnelStatus);

  const renderContent = () => {
    if (user?.status === 'suspended') {
      return <ViewSuspended contactEmail={contactEmail} />;
    }

    switch (funnelStatus) {
      case 'registered':
        return (
          <ViewRegistered
            contactEmail={contactEmail}
            stripeEnabled={stripeEnabled}
            whatsappUrl={whatsappUrl}
            onConsultantRequest={handleConsultantRequest}
            requesting={requesting}
          />
        );

      case 'level_tested':
        return (
          <ViewLevelTested
            user={user}
            contactEmail={contactEmail}
            onConsultantRequest={handleConsultantRequest}
            requesting={requesting}
            stripeEnabled={stripeEnabled}
            whatsappUrl={whatsappUrl}
          />
        );

      case 'consultant_requested':
        return <ViewConsultantRequested contactEmail={contactEmail} />;

      case 'awaiting_payment':
        return <ViewAwaitingPayment contactEmail={contactEmail} />;

      case 'payment_expired':
        return <ViewPaymentExpired contactEmail={contactEmail} stripeEnabled={stripeEnabled} />;

      case 'suspended':
        return <ViewSuspended contactEmail={contactEmail} />;

      default:
        return <ViewGenericPending contactEmail={contactEmail} />;
    }
  };

  return (
    <Shell funnelStatus={funnelStatus} showProgress={showProgress}>
      {renderContent()}
      <div className="px-8 pb-6 text-center border-t border-[#E8EAF0] pt-4">
        <Link
          to="/"
          className="text-xs text-[#1A2E6E]/40 hover:text-[#1A2E6E] transition-colors"
        >
          ← Back to Website
        </Link>
      </div>
    </Shell>
  );
};

export default StudentPending;
